// Klient pro Server-Sent Events přes POST (EventSource umí jen GET): čte `text/event-stream`
// z fetch odpovědi a každou událost předá jako naparsovaný JSON z pole `data`.
import { API_PREFIX, APP_VERSION_HEADER } from '@wac-buddy/shared'
import { ApiClientError, BASE, errorFromResponse } from '@/api/client'

export interface StreamSseOptions<T> {
  onEvent: (event: T) => void
  signal?: AbortSignal
}

/** Jeden blok SSE (mezi prázdnými řádky) na JSON z `data`; bloky bez dat (komentáře, ping) vrací null. */
function parseBlock(block: string): unknown {
  const data: string[] = []
  for (const line of block.split('\n')) {
    if (!line.startsWith('data:')) continue
    const value = line.slice(5)
    data.push(value.startsWith(' ') ? value.slice(1) : value)
  }
  if (!data.length) return null
  try {
    return JSON.parse(data.join('\n'))
  } catch {
    return null
  }
}

/**
 * Pošle POST s JSON tělem a streamuje odpověď. Ne-2xx odpověď (například 429 `rate_limited`)
 * skončí `ApiClientError` stejně jako `api()`. Přerušení přes `signal` odmítne promise s `AbortError`.
 */
export async function streamSse<T = unknown>(
  path: string,
  body: unknown,
  { onEvent, signal }: StreamSseOptions<T>,
): Promise<void> {
  const res = await fetch(`${BASE}${API_PREFIX}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      [APP_VERSION_HEADER]: __APP_VERSION__,
    },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) throw await errorFromResponse(res)
  if (!res.body) {
    throw new ApiClientError('internal', 'Server nevrátil proud dat.', res.status)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const drain = (final: boolean) => {
    // Konce řádků sjednotit na \n; osamělé \r na konci chunku počká na další (může jít o \r\n).
    const keep = !final && buffer.endsWith('\r') ? '\r' : ''
    buffer = buffer.slice(0, buffer.length - keep.length).replace(/\r\n?/g, '\n') + keep
    let end: number
    while ((end = buffer.indexOf('\n\n')) !== -1) {
      const event = parseBlock(buffer.slice(0, end))
      buffer = buffer.slice(end + 2)
      if (event !== null) onEvent(event as T)
    }
    if (final && buffer.trim()) {
      const event = parseBlock(buffer)
      buffer = ''
      if (event !== null) onEvent(event as T)
    }
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      drain(false)
    }
    buffer += decoder.decode()
    drain(true)
  } finally {
    // Při chybě nebo přerušení uvolní spojení; po dočtení je to bez účinku.
    reader.cancel().catch(() => undefined)
  }
}
